"use client";

import TFLink from "@/Web/Components/TFLink/TFLink";
import TFBadge from "@/Web/Components/TFBadge/TFBadge";
import TFLayoutHomeSection from "@/Web/Components/TFLayoutHomeSection/TFLayoutHomeSection";
import TFHorizontalSlider from "@/Web/Components/TFHorizontalSlider/TFHorizontalSlider";
import { useMemo, useState, useEffect } from "react";
import { useParams } from "next/navigation";
import { HiPlay } from "react-icons/hi";
import { IoEyeSharp, IoTimeSharp, IoCalendarSharp } from "react-icons/io5";
import { useDirection } from "@/utils/useDirection";
import { useTranslations } from "next-intl";
import {
  APIDataPublishedSpaceVideo,
  getSpaceTitle,
  loadPublishedSpaceVideos,
} from "@/utils/hubSpaceVideos";

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}ч ${minutes}м`;
  }
  return `${minutes}м`;
};

const formatTimeAgo = (
  dateString: string,
  t: ReturnType<typeof useTranslations>,
): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return t("today");
  if (diffDays === 1) return t("yesterday");
  if (diffDays < 7) return t("daysAgo", { days: diffDays });
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return t("weeksAgo", { weeks });
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return t("monthsAgo", { months });
  }
  const years = Math.floor(diffDays / 365);
  return t("yearsAgo", { years });
};

const VideoCard = ({
  video,
  href,
}: {
  video: APIDataPublishedSpaceVideo;
  href: string;
}) => {
  const direction = useDirection();
  const isRTL = direction === "rtl";
  const t = useTranslations("web.pages.home.sections.videos");
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <TFLink
      href={href}
      className="bg-black/5 rounded-lg hover:bg-black/10 transition-colors h-full flex flex-col group"
    >
      {/* Placeholder для видео */}
      <div className="relative w-full aspect-video bg-black/10 rounded-t-lg overflow-hidden">
        {video.coverUrl && !imageError && (
          <img
            src={video.coverUrl}
            alt={video.title}
            className={`w-full h-full object-cover ${imageLoaded ? "opacity-100" : "opacity-0"}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <HiPlay className="w-16 h-16 text-white/80 group-hover:text-white transition-colors" />
        </div>
        {(video.tags?.length ?? 0) > 0 && (
          <div className={`absolute top-2 ${isRTL ? "right-2" : "left-2"}`}>
            <TFBadge title={video.tags?.[0]?.title || ""} style="gray" />
          </div>
        )}
      </div>

      <div className="p-3 flex flex-col flex-grow">
        <h3 className="text-base font-semibold text-black line-clamp-2 group-hover:text-black/70 transition-colors mb-2">
          {video.title}
        </h3>

        <div className="text-sm text-black/60 mb-3">
          {getSpaceTitle(video.space)}
        </div>

        <div className="flex items-center gap-2 text-xs text-black/50 mt-auto">
          {video.duration && (
            <>
              <div className="flex items-center gap-1">
                <IoTimeSharp className="w-3 h-3 text-black/40" />
                <span>{formatDuration(video.duration)}</span>
              </div>
              <div className="w-0.5 h-0.5 rounded-full bg-black/20"></div>
            </>
          )}
          {video.metrics.views !== null && (
            <>
              <div className="flex items-center gap-1">
                <IoEyeSharp className="w-3 h-3 text-black/40" />
                <span>{video.metrics.views.toLocaleString("en-US")}</span>
              </div>
              <div className="w-0.5 h-0.5 rounded-full bg-black/20"></div>
            </>
          )}
          <div className="flex items-center gap-1">
            <IoCalendarSharp className="w-3 h-3 text-black/40" />
            <span>{formatTimeAgo(video.created, t)}</span>
          </div>
        </div>
      </div>
    </TFLink>
  );
};

export default function SectionVideos() {
  const t = useTranslations("web.pages.home.sections.videos");
  const params = useParams<{ locale: string }>();
  const locale = params?.locale || "en";
  const [itemsPerView, setItemsPerView] = useState(2);
  const [videos, setVideos] = useState<APIDataPublishedSpaceVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const updateItemsPerView = () => {
      setItemsPerView(window.innerWidth < 768 ? 1 : 2);
    };

    updateItemsPerView();
    window.addEventListener("resize", updateItemsPerView);
    return () => window.removeEventListener("resize", updateItemsPerView);
  }, []);

  useEffect(() => {
    const loadVideos = async () => {
      try {
        setIsLoading(true);
        setVideos(await loadPublishedSpaceVideos(locale as UILocale));
      } catch (error) {
        console.error("Failed to load videos:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (locale) {
      loadVideos();
    }
  }, [locale]);

  const featuredVideos = useMemo(() => {
    return [...videos]
      .sort(
        (a, b) =>
          new Date(b.created).getTime() - new Date(a.created).getTime(),
      )
      .slice(0, 6);
  }, [videos]);

  if (isLoading || featuredVideos.length === 0) {
    return null;
  }

  return (
    <TFLayoutHomeSection
      moreButton={{
        title: t("allVideos"),
        href: "/videos",
      }}
      sideInfo={{
        title: t("title"),
        description: t("description"),
        statistics: [{ title: t("videos"), value: videos.length }],
        position: "right",
      }}
    >
      <TFHorizontalSlider
        items={featuredVideos}
        renderItem={(video) => (
          <VideoCard video={video} href={`/videos/${video.ref}`} />
        )}
        itemWidth={320}
        gap={16}
        autoScroll={false}
        showControls={true}
        itemsPerView={itemsPerView}
        showDots={true}
      />
    </TFLayoutHomeSection>
  );
}
